import React, { useState } from 'react';
import { Pencil, ChevronDown } from 'lucide-react';
import { useDialog } from '@hooks/useDialog';
import type { ClinicDetailDto } from '../types/clinic';
import EditOperatingHoursModal from './EditOperatingHoursModal';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicInfoSection.css';
import './OperatingHoursSection.css';

interface OperatingHoursSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

const OperatingHoursSection: React.FC<Readonly<OperatingHoursSectionProps>> = ({ clinic, onUpdate }) => {
  const editModal = useDialog();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const formatTime = (time?: string | null): string => {
    if (!time) return 'Closed';
    // Convert "08:00" or "08:00:00" to "8:00 AM"
    try {
      const [hours, minutes] = time.split(':');
      const h = parseInt(hours);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  const formatDayHours = (start?: string | null, end?: string | null): string => {
    if (!start && !end) return 'Closed';
    if (!start || !end) return 'Closed';
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const days = [
    { label: 'Monday', start: clinic.monStart, end: clinic.monEnd },
    { label: 'Tuesday', start: clinic.tueStart, end: clinic.tueEnd },
    { label: 'Wednesday', start: clinic.wedStart, end: clinic.wedEnd },
    { label: 'Thursday', start: clinic.thurStart, end: clinic.thurEnd },
    { label: 'Friday', start: clinic.friStart, end: clinic.friEnd },
    { label: 'Saturday', start: clinic.satStart, end: clinic.satEnd },
    { label: 'Sunday', start: clinic.sunStart, end: clinic.sunEnd },
  ];

  // A closed day and a never-entered day are both NULL in the database, so the
  // distinction only exists at whole-week granularity: all 14 columns NULL
  // means no hours were ever recorded — showing "Closed" x7 misreads a data
  // gap as a fact (SLV2-3154).
  const hasAnyHours = days.some(day => day.start || day.end);

  return (
    <div className="info-section operating-hours-section">
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Operating Hours</h3>
        </button>
        <div className="info-section-actions">
          <RequirePermission permission="UserCanEditClinicOperatingHours">
            <button
              type="button"
              className="svc-edit-btn"
              onClick={editModal.open}
              title="Edit Operating Hours"
            >
              <Pencil size={14} />
              Edit
            </button>
          </RequirePermission>
        </div>
      </div>

      {!isCollapsed && (
        hasAnyHours ? (
          <div className="hours-display-grid">
            {days.map(day => (
              <div key={day.label} className="hours-display-row">
                <span className="day-label">{day.label}</span>
                <span className={`day-hours ${!day.start || !day.end ? 'closed' : ''}`}>
                  {formatDayHours(day.start, day.end)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="hours-not-set">
            No hours on record — operating hours have not been entered for this clinic yet.
          </div>
        )
      )}

      {editModal.isOpen && (
        <EditOperatingHoursModal
          clinic={clinic}
          onClose={editModal.close}
          onSave={() => {
            editModal.close();
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

export default OperatingHoursSection;
